import { ArrowLeft, Save } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { toast } from 'sonner'
import { useUserRol, useTienePermiso} from '../App'
import { actualizarAceptado, guardarPrueba } from '../services/pruebasService'
import { RechazoModal } from "./RechazoModal"
import { Button } from "./ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card"
import { Input } from "./ui/input"
import { Switch } from "./ui/switch"
import { Textarea } from "./ui/textarea"

interface MET7219FormData {
  fecha: string
  resultado: string
  realizo: string
  superviso: string
  observaciones: string
  mostrarObservaciones: boolean
  numero_solicitud?: string // NUEVO
}

interface MET7219FormProps {
  mode: "create" | "view" | "edit"
  initialData?: Partial<MET7219FormData>
  onSave?: (data: MET7219FormData) => void
  onCancel?: () => void
  realizoUser?: string // NUEVO: nombre del usuario
  onRefreshBackend?: () => void
  pedidoRowId?: string
  onAutosave?: (result: { rowId: string; nomId: string }) => void
}

export function MET7219Form({
  mode,
  initialData,
  onSave,
  onCancel,
  realizoUser, // NUEVO
  onRefreshBackend,
  pedidoRowId,
  onAutosave,
}: MET7219FormProps) {
  const initialDataObj = Array.isArray(initialData) ? initialData[0] : initialData || {};
  const globalRol = typeof useUserRol === 'function' ? useUserRol() : undefined;
  const puedeRevisar = useTienePermiso('aprobar_rechazar_resultado');
  const effectiveRol = globalRol;
  const aceptadoRaw = (initialDataObj as any)?.ACEPTADO ?? (initialDataObj as any)?.aceptado;
  const aceptadoValue = typeof aceptadoRaw === 'string' ? aceptadoRaw.toLowerCase().trim() : '';
  const isAceptado = aceptadoValue === 'aceptado' || aceptadoValue === 'rechazado';
  const [autosaveRowId, setAutosaveRowId] = useState<string>((initialDataObj as any)?.ROW_ID || (initialDataObj as any)?.row_id || '');
  const [autosaveNomId, setAutosaveNomId] = useState<string>((initialDataObj as any)?.nomId || '');
  const rowId = autosaveRowId;
  const nomId = autosaveNomId;
  const pedidoValue = (initialDataObj as any)?.PEDIDO || '';
  const pruebaValue = (initialDataObj as any)?.PRUEBA || '';
  const [editMode, setEditMode] = useState(false);
  const [estatus, setEstatus] = useState<string>(aceptadoValue);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rechazoModalOpen, setRechazoModalOpen] = useState(false);
  const [rechazoLoading, setRechazoLoading] = useState(false);

  const handleEditar = () => setEditMode(true);

  const handleAccion = async (accion: 'aceptado' | 'rechazado', razonRechazo?: string) => {
    const confirmMsg = accion === 'aceptado'
      ? 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres aceptar la prueba MET-7.2-19?'
      : 'Al continuar, esta acción no podrá editarse ni borrarse. ¿Estás seguro de que quieres rechazar la prueba MET-7.2-19?';
    if (accion === 'rechazado' && !razonRechazo) {
      setRechazoModalOpen(true);
      return;
    }
    if (!window.confirm(confirmMsg)) return;
    setLoading(true);
    setError(null);
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || '',
      ACEPTAR: accion === 'aceptado' ? 'aceptado' : 'rechazado',
      ACEPTADO: accion,
      ...(accion === 'rechazado' ? { RAZON_RECHAZO: razonRechazo } : {})
    };
    try {
      if (accion === 'rechazado') setRechazoLoading(true);
      // Evita que un autoguardado pendiente o en vuelo se resuelva despues de
      // aceptar/rechazar y revierta `estado` a 'borrador' (ver guardarPrueba en pruebasService.ts).
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) { await new Promise(r => setTimeout(r, 100)) }
      await actualizarAceptado(rowId, accion, accion === 'rechazado' ? (payload as any).RAZON_RECHAZO : undefined, (payload as any).superviso, nomId);
      setEstatus(accion);
      setEditMode(false);
      setRechazoModalOpen(false);
      setRechazoLoading(false);
      toast.success(`Prueba ${accion === 'aceptado' ? 'aceptada' : 'rechazada'} correctamente.`);
      if (typeof onRefreshBackend === 'function') setTimeout(() => onRefreshBackend(), 300);
    } catch (err: any) {
      setRechazoLoading(false);
      setError('Error al enviar al backend: ' + (err.message || err));
    } finally {
      setLoading(false);
    }
  };

  const handleGuardarEdicion = async () => {
    if (!window.confirm('¿Seguro que quieres guardar y aceptar la edición de MET-7.2-19? Esta acción no se puede deshacer.')) return;
    setLoading(true);
    setError(null);
    const payload = {
      ...formData,
      ROW_ID: rowId,
      PEDIDO: pedidoValue,
      PRUEBA: pruebaValue,
      superviso: realizoUser || '',
      ACEPTAR: 'aceptado',
      ACEPTADO: 'aceptado'
    };
    try {
      onSave({ ...payload, _EDIT_MODE: true } as any);
    } catch (err) {
      console.error('Error al guardar edición:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (mode === 'view' && effectiveRol !== 'ADMIN') {
      setEditMode(false);
    }
  }, [effectiveRol, mode]);

  const [formData, setFormData] = useState<MET7219FormData>({
    fecha: "",
    resultado: "",
    realizo: "",
    superviso: "",
    observaciones: "",
    ...initialData,
    mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true,
  })

  // El modo edición debe permitir editar los campos
  const isReadOnly = mode === 'create' ? false : (mode === "view" && !editMode);

  // Si realizoUser cambia y estamos creando, actualiza realizo
  // (igual que en otros formularios)
  useEffect(() => {
    if (mode === "create" && realizoUser) {
      setFormData(prev => ({ ...prev, realizo: realizoUser }))
    }
  }, [realizoUser, mode])

  // Sincroniza el estado con initialData cuando cambia (para modo revisión)
  const initialDataSyncedRef = useRef(false)
  useEffect(() => {
    if (initialDataSyncedRef.current) return
    initialDataSyncedRef.current = true
    if (initialData) {
      setFormData(prev => ({ ...prev, ...initialData, mostrarObservaciones: (initialData as any)?.MOSTRAR_OBSERVACIONES === true }))
    }
  }, [initialData])

  const handleInputChange = (field: keyof MET7219FormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const formPayload = () => ({
    fecha: formData.fecha,
    resultado: formData.resultado,
    realizo: formData.realizo,
    superviso: formData.superviso,
    observaciones: formData.observaciones,
    numero_solicitud: formData.numero_solicitud,
  })

  // ── Autosave (borrador) ──────────────────────────────────────────────────
  const autosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isFirstAutosaveRenderRef = useRef(true)
  const autosaveInFlightRef = useRef(false)
  const rowIdRef = useRef(rowId)
  const nomIdRef = useRef(nomId)
  useEffect(() => { rowIdRef.current = rowId }, [rowId])
  useEffect(() => { nomIdRef.current = nomId }, [nomId])

  const doAutosave = async () => {
    if (autosaveInFlightRef.current) return
    autosaveInFlightRef.current = true
    try {
      const { pruebaId, nomId: savedNomId } = await guardarPrueba(
        'TIPO DE ACABADO MET-7.2-19',
        pedidoRowId || '',
        formPayload(),
        nomIdRef.current || undefined,
        rowIdRef.current || undefined,
        true,
      )
      setAutosaveRowId(String(pruebaId))
      setAutosaveNomId(savedNomId)
      rowIdRef.current = String(pruebaId)
      nomIdRef.current = savedNomId
      onAutosave?.({ rowId: String(pruebaId), nomId: savedNomId })
    } catch (err) {
      console.warn('Autosave de borrador falló (silencioso):', err)
    } finally {
      autosaveInFlightRef.current = false
    }
  }

  useEffect(() => {
    if (isFirstAutosaveRenderRef.current) {
      isFirstAutosaveRenderRef.current = false
      return
    }
    if (mode === 'view' || isAceptado) return

    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    autosaveTimerRef.current = setTimeout(() => {
      autosaveTimerRef.current = null
      doAutosave()
    }, 1500)

    return () => {
      if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, mode, isAceptado])

  // Si hay un autoguardado pendiente (debounce de 1.5s) y el usuario navega hacia
  // atrás antes de que se dispare, se cancelaba sin guardarse nunca. Al salir,
  // se ejecuta de inmediato ese guardado pendiente con los datos más recientes.
  const handleBackFlush = () => {
    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current)
      autosaveTimerRef.current = null
      if (mode !== 'view' && !isAceptado) {
        doAutosave()
      }
    }
    onCancel?.()
  }

  const [guardando, setGuardando] = useState(false)

  const handleSave = async () => {
    if (guardando) return
    setGuardando(true)
    try {
      // Cancela cualquier autoguardado pendiente y espera a que termine el que ya
      // esté en curso, para que el guardado explícito sea siempre el último en
      // escribir (si no, un autoguardado tardío puede revertir esto a "borrador").
      if (autosaveTimerRef.current) { clearTimeout(autosaveTimerRef.current); autosaveTimerRef.current = null }
      while (autosaveInFlightRef.current) {
        await new Promise(r => setTimeout(r, 100))
      }
      if (onSave) {
        onSave(formData)
      }
    } finally {
      setGuardando(false)
    }
  }

  const getTitle = () => {
    switch (mode) {
      case "create":
        return "Crear TIPO DE ACABADO MET-7.2-19"
      case "edit":
        return "Editar TIPO DE ACABADO MET-7.2-19"
      case "view":
        return "Ver TIPO DE ACABADO MET-7.2-19"
      default:
        return "TIPO DE ACABADO MET-7.2-19"
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleBackFlush}
            className="gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Button>
          <h1>{getTitle()}</h1>
        </div>
        {/* Elimina el botón de guardar superior en modo create y edit, solo aparece en modo view y cuando no está en editMode */}
        {!isReadOnly && mode === 'view' && !editMode && (
          <Button onClick={handleSave} className="gap-2" disabled={guardando}>
            <Save className="h-4 w-4" />
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-center text-lg">
            DETERMINACIÓN DEL TIPO DE ACABADO MET-7.2-19
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* NUEVO: Número de solicitud */}
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1" htmlFor="numero_solicitud">NÚMERO DE SOLICITUD</label>
            <Input
              id="numero_solicitud"
              type="text"
              placeholder="Ej. 12345"
              value={formData.numero_solicitud || ''}
              onChange={e => handleInputChange('numero_solicitud', e.target.value)}
              disabled={isReadOnly}
            />
          </div>
          {/* Tabla principal */}
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border">
              <tbody>
                {/* FECHA */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium w-1/4">
                    FECHA:
                  </td>
                  <td className="border border-border p-1">
                    <Input
                      type="date"
                      value={formData.fecha}
                      onChange={(e) => handleInputChange('fecha', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent"
                    />
                  </td>
                </tr>

                {/* RESULTADO y REALIZÓ */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium">
                    RESULTADO:
                  </td>
                  <td className="border border-border p-2 bg-muted font-medium">
                    REALIZÓ:
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-1 align-top">
                    <Textarea
                      placeholder="Resultado de la prueba..."
                      value={formData.resultado}
                      onChange={(e) => handleInputChange('resultado', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent min-h-[120px] resize-none"
                    />
                  </td>
                  <td className="border border-border p-1 align-top">
                    <Input
                      type="text"
                      placeholder="Nombre del responsable"
                      value={formData.realizo}
                      readOnly
                      className="border-0 bg-muted-foreground/10 cursor-not-allowed"
                    />
                    <div className="mt-2 border-t border-border pt-2">
                      <div className="bg-muted p-2 font-medium">
                        SUPERVISÓ:
                      </div>
                      <div className="p-1">
                        <Input
                          type="text"
                          placeholder="Supervisor"
                          value={formData.superviso}
                          readOnly
                          className="border-0 bg-muted-foreground/10 cursor-not-allowed"
                        />
                      </div>
                    </div>
                  </td>
                </tr>

                {/* OBSERVACIONES */}
                <tr>
                  <td className="border border-border p-2 bg-muted font-medium flex items-center gap-2" colSpan={2}>
                    OBSERVACIONES:
                    <span className="ml-auto flex items-center gap-1.5 text-xs font-normal text-muted-foreground">
                      Mostrar en Reporte Cliente
                      <Switch checked={formData.mostrarObservaciones === true} disabled={isReadOnly}
                        onCheckedChange={(checked) => handleInputChange('mostrarObservaciones', checked)} />
                    </span>
                  </td>
                </tr>
                <tr>
                  <td className="border border-border p-1" colSpan={2}>
                    <Textarea
                      placeholder="Observaciones adicionales sobre la prueba..."
                      value={formData.observaciones}
                      onChange={(e) => handleInputChange('observaciones', e.target.value)}
                      disabled={isReadOnly}
                      className="border-0 bg-transparent min-h-[80px]"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <div className="flex justify-end gap-4">
        <Button variant="outline" onClick={handleBackFlush} disabled={loading}>
          Cancelar
        </Button>
        {mode === 'view' && !editMode && !isAceptado && puedeRevisar && (
          <>
            <Button variant="default" onClick={() => handleAccion('aceptado')} disabled={loading}>Aceptar</Button>
            <Button variant="destructive" onClick={() => setRechazoModalOpen(true)} disabled={loading}>Rechazar</Button>
            <Button variant="secondary" onClick={handleEditar} disabled={loading}>Editar</Button>
          </>
        )}
        {mode === 'view' && editMode && !isAceptado && puedeRevisar && (
          <Button variant="default" onClick={handleGuardarEdicion} disabled={loading}>Guardar Edición</Button>
        )}
        {(mode === 'create' || mode === 'edit') && (
          <Button onClick={handleSave} disabled={loading || guardando}>
            <Save className="h-4 w-4 mr-2" />
            {guardando ? 'Guardando...' : 'Guardar'}
          </Button>
        )}
      </div>
      {error && <div className="text-red-500 mt-2">{error}</div>}
      <RechazoModal
        open={rechazoModalOpen}
        onClose={() => setRechazoModalOpen(false)}
        loading={rechazoLoading}
        onSubmit={(razon) => handleAccion('rechazado', razon)}
      />
    </div>
  )
}